import React, { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Checkbox,
  Select,
  MenuItem,
  Switch,
} from '@mui/material'
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Badge as BadgeIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { extractErrorMessage } from '../../utils/errorHandler'
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel'

type JobGrade = '部長' | '課長' | '班長' | '役職なし'
type DepartmentType = 'tech' | 'sales' | 'admin'

interface JobTitle {
  job_title_name: string
  job_title_name_en: string | null
  job_grade: JobGrade
  responsibility: string | null
  display_order: number
  is_active: boolean
  department_types: DepartmentType[]
  assigned_employees: number
}

interface JobTitleFormData {
  job_title_name: string
  job_title_name_en: string
  job_grade: JobGrade
  responsibility: string
  display_order: number | ''
  department_types: DepartmentType[]
  is_active: boolean
}

const JOB_GRADES: JobGrade[] = ['部長', '課長', '班長', '役職なし']

const DEPARTMENT_TYPE_LABELS: Record<DepartmentType, string> = {
  tech: '技術部門',
  sales: '営業部門',
  admin: '管理部門',
}

const JobTitleManagementPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingJobTitle, setEditingJobTitle] = useState<JobTitle | null>(null)
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<JobTitleFormData>({
    defaultValues: {
      job_title_name: '',
      job_title_name_en: '',
      job_grade: '役職なし',
      responsibility: '',
      display_order: '',
      department_types: [],
      is_active: true,
    },
  })

  const { data: jobTitlesData, isLoading, refetch } = useQuery({
    queryKey: ['job-titles', showInactive],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: JobTitle[] }>(
        `/job-titles${showInactive ? '?include_inactive=true' : ''}`
      )
      return response.data.data
    },
  })

  const invalidateJobTitles = () => {
    queryClient.invalidateQueries({ queryKey: ['job-titles'] })
    queryClient.invalidateQueries({ queryKey: ['job-titles-active'] })
  }

  const createMutation = useMutation({
    mutationFn: async (data: JobTitleFormData) => {
      return await api.post('/job-titles', {
        job_title_name: data.job_title_name.trim(),
        job_title_name_en: data.job_title_name_en.trim() || null,
        job_grade: data.job_grade,
        responsibility: data.responsibility.trim() || null,
        display_order: data.display_order === '' ? undefined : Number(data.display_order),
        department_types: data.department_types,
      })
    },
    onSuccess: () => {
      toast.success('役職を追加しました')
      invalidateJobTitles()
      setDialogOpen(false)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '役職の追加に失敗しました'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: JobTitleFormData) => {
      return await api.put(`/job-titles/${encodeURIComponent(data.job_title_name)}`, {
        job_title_name_en: data.job_title_name_en.trim() || null,
        job_grade: data.job_grade,
        responsibility: data.responsibility.trim() || null,
        display_order: data.display_order === '' ? undefined : Number(data.display_order),
        department_types: data.department_types,
        is_active: data.is_active,
      })
    },
    onSuccess: () => {
      toast.success('役職を更新しました')
      invalidateJobTitles()
      setDialogOpen(false)
      setEditingJobTitle(null)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '役職の更新に失敗しました'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (jobTitleName: string) => {
      return await api.delete<{ success: boolean; message: string }>(
        `/job-titles/${encodeURIComponent(jobTitleName)}`
      )
    },
    onSuccess: (response) => {
      toast.success(response.data.message)
      invalidateJobTitles()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '役職の削除に失敗しました'))
    },
  })

  const handleEdit = (jobTitle: JobTitle) => {
    setEditingJobTitle(jobTitle)
    setIsCreateMode(false)
    reset({
      job_title_name: jobTitle.job_title_name,
      job_title_name_en: jobTitle.job_title_name_en || '',
      job_grade: jobTitle.job_grade,
      responsibility: jobTitle.responsibility || '',
      display_order: jobTitle.display_order,
      department_types: jobTitle.department_types,
      is_active: jobTitle.is_active,
    })
    setDialogOpen(true)
  }

  const handleCreate = () => {
    setEditingJobTitle(null)
    setIsCreateMode(true)
    reset({
      job_title_name: '',
      job_title_name_en: '',
      job_grade: '役職なし',
      responsibility: '',
      display_order: '',
      department_types: [],
      is_active: true,
    })
    setDialogOpen(true)
  }

  const handleDelete = async (jobTitle: JobTitle) => {
    const message =
      jobTitle.assigned_employees > 0
        ? `役職「${jobTitle.job_title_name}」は現在 ${jobTitle.assigned_employees} 名に割り当てられています。削除の代わりに無効化されます。続行しますか？`
        : `役職「${jobTitle.job_title_name}」を削除しますか？この操作は取り消せません。`
    if (window.confirm(message)) {
      await deleteMutation.mutateAsync(jobTitle.job_title_name)
    }
  }

  const onSubmit = async (data: JobTitleFormData) => {
    if (isCreateMode) {
      await createMutation.mutateAsync(data)
    } else {
      await updateMutation.mutateAsync(data)
    }
  }

  const jobTitles = jobTitlesData || []

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BadgeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            役職マスタ管理
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label="無効な役職も表示"
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()}>
            更新
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            新規
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          <strong>役職マスタ:</strong> 就業規程 附則1「職位定義」に対応する役職を管理します。
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          ※ 職級と適用部署の組み合わせが、所属・職位管理の役職名の選択肢になります。
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          ※ 所属履歴や評価データで使用中の役職は、削除ではなく無効化されます。
        </Typography>
      </Alert>

      {isLoading ? (
        <Typography>読み込み中...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="right">表示順</TableCell>
                <TableCell>役職名</TableCell>
                <TableCell>英語名</TableCell>
                <TableCell align="center">職級</TableCell>
                <TableCell>適用部署</TableCell>
                <TableCell align="right">在任者</TableCell>
                <TableCell align="center">状態</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobTitles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    役職データがありません
                  </TableCell>
                </TableRow>
              ) : (
                jobTitles.map((jobTitle) => (
                  <TableRow key={jobTitle.job_title_name}>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {jobTitle.display_order}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {jobTitle.job_title_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {jobTitle.job_title_name_en || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={jobTitle.job_grade} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {jobTitle.department_types.map((type) => (
                          <Chip key={type} label={DEPARTMENT_TYPE_LABELS[type]} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{jobTitle.assigned_employees}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={jobTitle.is_active ? '有効' : '無効'}
                        size="small"
                        color={jobTitle.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="編集">
                        <IconButton size="small" onClick={() => handleEdit(jobTitle)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={jobTitle.assigned_employees > 0 ? '使用中のため無効化されます' : '削除'}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(jobTitle)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreateMode ? '役職の新規追加' : `役職の編集: ${editingJobTitle?.job_title_name}`}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <Controller
                name="job_title_name"
                control={control}
                rules={{
                  required: '役職名は必須です',
                  maxLength: { value: 100, message: '役職名は100文字以内で入力してください' },
                }}
                render={({ field }) => (
                  <Box>
                    <RequiredFieldLabel label="役職名" />
                    <TextField
                      {...field}
                      fullWidth
                      disabled={!isCreateMode}
                      error={!!errors.job_title_name}
                      helperText={
                        errors.job_title_name?.message ||
                        (isCreateMode ? '' : '役職名は所属履歴や評価データから参照されるため変更できません')
                      }
                      placeholder="例: プロジェクトリーダー"
                    />
                  </Box>
                )}
              />

              <Controller
                name="job_title_name_en"
                control={control}
                rules={{ maxLength: { value: 100, message: '英語名は100文字以内で入力してください' } }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="英語名（任意）"
                    error={!!errors.job_title_name_en}
                    helperText={errors.job_title_name_en?.message}
                    placeholder="例: Project Leader"
                  />
                )}
              />

              <Controller
                name="job_grade"
                control={control}
                rules={{ required: '職級は必須です' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.job_grade}>
                    <RequiredFieldLabel label="職級" />
                    <Select {...field}>
                      {JOB_GRADES.map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          {grade}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.job_grade?.message || '評価の重み係数は職級で決まります'}
                    </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="department_types"
                control={control}
                rules={{
                  validate: (value) =>
                    (value && value.length > 0) || '適用部署を1つ以上選択してください',
                }}
                render={({ field }) => (
                  <FormControl error={!!errors.department_types}>
                    <RequiredFieldLabel label="適用部署" />
                    <FormGroup row>
                      {(Object.keys(DEPARTMENT_TYPE_LABELS) as DepartmentType[]).map((type) => (
                        <FormControlLabel
                          key={type}
                          control={
                            <Checkbox
                              checked={field.value.includes(type)}
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, type]
                                    : field.value.filter((t) => t !== type)
                                )
                              }}
                            />
                          }
                          label={DEPARTMENT_TYPE_LABELS[type]}
                        />
                      ))}
                    </FormGroup>
                    <FormHelperText>
                      {errors.department_types?.message ||
                        'この役職を使える部署タイプ。部長クラスは全部門共通です'}
                    </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="responsibility"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="責任定義（任意）"
                    placeholder="就業規程 附則1「職位定義」の内容"
                  />
                )}
              />

              <Controller
                name="display_order"
                control={control}
                rules={{
                  validate: (value) =>
                    value === '' || Number(value) >= 1 || '表示順は1以上で入力してください',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="表示順（任意）"
                    error={!!errors.display_order}
                    helperText={errors.display_order?.message || '未入力の場合は末尾に追加されます'}
                  />
                )}
              />

              {!isCreateMode && (
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      }
                      label="有効（無効にすると新規の所属登録で選択できなくなります）"
                    />
                  )}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {isCreateMode ? '追加' : '更新'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}

export default JobTitleManagementPage
